/**
 * GDPR and PII Redaction Helpers
 * Sprint 6: Security Hardening
 *
 * These helpers ensure PII (Personally Identifiable Information) is redacted
 * before being logged or sent to external services (e.g., Sentry, audit logs).
 *
 * IMPORTANT: Per CLAUDE.md - "Never log or send PII to console or logs"
 */

/**
 * Redact email address to show only first character and domain
 * @example redactEmail('john.doe@example.com') => 'j***@example.com'
 */
export function redactEmail(email: string | null | undefined): string {
  if (!email || typeof email !== 'string') {
    return '***@***.***';
  }

  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return '***@***.***';
  }

  const redactedLocal = localPart[0] + '***';
  return `${redactedLocal}@${domain}`;
}

/**
 * Redact user ID to show only first and last 3 characters
 * @example redactUserId('550e8400-e29b-41d4-a716-446655440000') => '550***000'
 */
export function redactUserId(userId: string | null | undefined): string {
  if (!userId || typeof userId !== 'string') {
    return '***';
  }

  if (userId.length <= 8) {
    return '***';
  }

  const first = userId.slice(0, 3);
  const last = userId.slice(-3);
  return `${first}***${last}`;
}

/**
 * Redact common PII patterns from text
 * Removes: emails, phone numbers, credit cards, social security numbers
 */
export function redactPII(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let redacted = text;

  // IMPORTANT: Order matters! Process longest/most specific patterns first
  // to avoid false matches (e.g., UUIDs before phone numbers)

  // Redact UUIDs FIRST (often used as user IDs)
  // Must come before phone regex to avoid matching UUID segments
  redacted = redacted.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    '[UUID_REDACTED]'
  );

  // Redact credit card numbers (16 digits with optional spaces/dashes)
  redacted = redacted.replace(
    /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
    '[CARD_REDACTED]'
  );

  // Redact email addresses
  redacted = redacted.replace(
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    '[EMAIL_REDACTED]'
  );

  // Redact phone numbers (various formats)
  // Full 10-digit format only to avoid false positives
  redacted = redacted.replace(
    /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
    '[PHONE_REDACTED]'
  );

  // Redact SSN/PPSN (Social Security / Personal Public Service Number)
  redacted = redacted.replace(
    /\b\d{3}-\d{2}-\d{4}\b/g, // US SSN format
    '[SSN_REDACTED]'
  );
  redacted = redacted.replace(
    /\b\d{7}[A-Z]{1,2}\b/g, // Irish PPSN format
    '[PPSN_REDACTED]'
  );

  return redacted;
}

/**
 * Sanitize data for audit logging
 * Redacts PII fields while preserving structure for debugging
 */
export function sanitizeForAuditLog(data: any): any {
  if (!data) {
    return data;
  }

  if (typeof data === 'string') {
    return redactPII(data);
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeForAuditLog);
  }

  if (typeof data === 'object') {
    const sanitized: any = {};

    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Redact sensitive fields by key name
      if (
        lowerKey.includes('email') ||
        lowerKey.includes('phone') ||
        lowerKey.includes('password') ||
        lowerKey.includes('token') ||
        lowerKey.includes('secret') ||
        lowerKey.includes('api_key') ||
        lowerKey.includes('apikey')
      ) {
        sanitized[key] = '[REDACTED]';
      }
      // Redact user IDs
      else if (lowerKey === 'user_id' || lowerKey === 'userid') {
        sanitized[key] = typeof value === 'string' ? redactUserId(value) : '[REDACTED]';
      }
      // Recursively sanitize nested objects
      else if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitizeForAuditLog(value);
      }
      // Redact string values that might contain PII
      else if (typeof value === 'string') {
        sanitized[key] = redactPII(value);
      }
      // Keep other primitive values as-is
      else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  return data;
}

/**
 * Redact first name to show only first character
 * @example redactFirstName('John') => 'J***'
 */
export function redactFirstName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') {
    return '***';
  }

  if (name.length === 0) {
    return '***';
  }

  if (name.length === 1) {
    return name;
  }

  return name[0] + '***';
}

/**
 * Redact full name to show only initials
 * @example redactFullName('John Doe') => 'J.D.'
 */
export function redactFullName(fullName: string | null | undefined): string {
  if (!fullName || typeof fullName !== 'string') {
    return '***';
  }

  const parts = fullName.trim().split(/\s+/);
  const initials = parts.map(part => part[0]?.toUpperCase() || '').filter(Boolean);

  return initials.length > 0 ? initials.join('.') + '.' : '***';
}

/**
 * Check if a string contains potential PII
 * Useful for validating that PII was properly redacted before logging
 */
export function containsPII(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }

  const piiPatterns = [
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/, // Email
    /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/, // Phone
    /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/, // Credit card
    /\b\d{3}-\d{2}-\d{4}\b/, // SSN
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i, // UUID
  ];

  return piiPatterns.some(pattern => pattern.test(text));
}

/**
 * Safe logger for production
 * Redacts PII before logging to console
 */
export function safeLog(message: string, data?: any): void {
  const sanitizedData = data ? sanitizeForAuditLog(data) : undefined;

  if (process.env.NODE_ENV === 'development') {
    console.log(message, sanitizedData);
  } else {
    // In production, only log if no PII detected
    const combinedText = `${message} ${JSON.stringify(sanitizedData || {})}`;
    if (!containsPII(combinedText)) {
      console.log(message, sanitizedData);
    } else {
      console.warn('Attempted to log data containing PII - blocked');
    }
  }
}

/**
 * Safe error logger for production
 * Redacts PII before logging errors
 */
export function safeErrorLog(message: string, error?: any): void {
  const sanitizedError = error ? sanitizeForAuditLog(error) : undefined;

  if (process.env.NODE_ENV === 'development') {
    console.error(message, sanitizedError);
  } else {
    // In production, always log errors but with sanitized data
    console.error(message, sanitizedError);
  }
}

// Export all helpers as a named export for backward compatibility
export const gdprHelpers = {
  redactEmail,
  redactUserId,
  redactPII,
  sanitizeForAuditLog,
  redactFirstName,
  redactFullName,
  containsPII,
  safeLog,
  safeErrorLog,
};
